<template>
  <div class="container py-5">
    <div class="row justify-content-center">
      <div class="col-md-8">
        <div class="card shadow-sm p-4">
          <h3 class="text-center mb-4">Change Password</h3>

          <form @submit.prevent="savePassword" novalidate>
            <!-- Current Password -->
            <div class="mb-3" style="position: relative;">
              <label class="form-label">Current Password *</label>
              <input
                v-model="form.current_password"
                :type="showCurrentPassword ? 'text' : 'password'"
                class="form-control"
                :class="{ 'is-invalid': v$.form.current_password.$error }"
                required
                style="padding-right: 40px;"
              />
              <span @click="showCurrentPassword = !showCurrentPassword" style="position: absolute; right: 15px; top: 38px; cursor: pointer; z-index: 10; font-size: 1.2em; color: #a0a0a0;">
                 <i :class="showCurrentPassword ? 'fa fa-eye-slash' : 'fa fa-eye'"></i>
              </span>
              <div class="invalid-feedback" v-if="v$.form.current_password.$error">
                <span v-if="!v$.form.current_password.required.$model">Current password is required.</span>
                <span v-else>Invalid current password.</span>
              </div>
            </div>

            <!-- New Password -->
            <div class="mb-3" style="position: relative;">
              <label class="form-label">New Password *</label>
              <input
                v-model="form.new_password"
                :type="showNewPassword ? 'text' : 'password'"
                class="form-control"
                :class="{ 'is-invalid': v$.form.new_password.$error }"
                required
                style="padding-right: 40px;"
              />
              <span @click="showNewPassword = !showNewPassword" style="position: absolute; right: 15px; top: 38px; cursor: pointer; z-index: 10; font-size: 1.2em; color: #a0a0a0;">
                 <i :class="showNewPassword ? 'fa fa-eye-slash' : 'fa fa-eye'"></i>
              </span>
              <div class="invalid-feedback" v-if="v$.form.new_password.$error">
                <span v-if="v$.form.new_password.required.$invalid">New password is required.</span>
                <span v-else-if="v$.form.new_password.minLength.$invalid">
                  Password must be at least 8 characters long.
                </span>
              </div>
            </div>

            <!-- Confirm Password -->
            <div class="mb-3" style="position: relative;">
              <label class="form-label">Confirm Password *</label>
              <input
                v-model="form.new_password_confirmation"
                :type="showNewPasswordConfirmation ? 'text' : 'password'"
                class="form-control"
                :class="{ 'is-invalid': v$.form.new_password_confirmation.$error }"
                required
                style="padding-right: 40px;"
              />
              <span @click="showNewPasswordConfirmation = !showNewPasswordConfirmation" style="position: absolute; right: 15px; top: 38px; cursor: pointer; z-index: 10; font-size: 1.2em; color: #a0a0a0;">
                 <i :class="showNewPasswordConfirmation ? 'fa fa-eye-slash' : 'fa fa-eye'"></i>
              </span>
              <div class="invalid-feedback" v-if="v$.form.new_password_confirmation.$error">
                <span v-if="v$.form.new_password_confirmation.required.$invalid">
                  Confirmation is required.
                </span>
                <span v-else-if="v$.form.new_password_confirmation.sameAsPassword.$invalid">
                  Passwords do not match.
                </span>
              </div>
            </div>

            <!-- Buttons -->
            <div class="d-flex justify-content-between align-items-center mt-4">
              <button class="btn btn-success" type="submit" :disabled="loading">
                <i class="fa fa-check-circle me-1"></i>
                {{ loading ? 'Saving...' : 'Save' }}
              </button>
              <button class="btn btn-secondary" type="button" @click="resetForm">
                <i class="fa fa-times-circle me-1"></i> Cancel
              </button>
            </div>
          </form>

          <!-- Alerts -->          
          <div v-if="errorMessage" class="alert alert-danger mt-4 text-center">
            {{ serverError['current_password'][0] || errorMessage }}
          </div>
          <div v-if="successMessage" class="alert alert-success mt-4 text-center">
            {{ successMessage }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { reactive, ref, computed } from 'vue'
import useVuelidate from '@vuelidate/core'
import { required, minLength, sameAs } from '@vuelidate/validators'
import { useAuthStore } from '@/stores/authStore'
import { api } from '@/plugins/axios'
import { toast } from 'vue3-toastify'

const store = useAuthStore()
const loading = ref(false)
const successMessage = ref('')
const errorMessage = ref('')
const serverError = ref([])

const showCurrentPassword = ref(false)
const showNewPassword = ref(false)
const showNewPasswordConfirmation = ref(false)

// Reactive form
const form = ref({
  current_password: '',
  new_password: '',
  new_password_confirmation: ''
})

const passwordRef = computed(() => form.value.new_password)

// Vuelidate rules
const rules = {
  form: {
    current_password: { required },
    new_password: { required, minLength: minLength(8) },
    // sameAs expects a function that returns the value to compare with
    new_password_confirmation: {
      required,
      sameAsPassword: sameAs(passwordRef, 'Passwords must match')
    }
  }
}

const v$ = useVuelidate(rules, { form })

// Reset form
function resetForm() {
  form.value.current_password = ''
  form.value.new_password = ''
  form.value.new_password_confirmation = ''  
  errorMessage.value = ''
  v$.value.$reset()
}

// Save (API call)
async function savePassword() {
  v$.value.$touch()  
  if (v$.value.$invalid) return

  loading.value = true
  successMessage.value = ''
  errorMessage.value = ''

  try {
      const response = await api.post('/change-password', {
         current_password: form.value.current_password,
         new_password: form.value.new_password,
         new_password_confirmation: form.value.new_password_confirmation
      })
      successMessage.value = response.data.message || 'Password changed successfully.'
      toast.success(successMessage.value)
    resetForm()
  } catch (err) {
      errorMessage.value = err.response?.data?.message || 'Failed to update password. Please try again.'
      serverError.value = err.response?.data?.results
      toast.error(serverError.value['current_password'][0] || errorMessage.value) 
  } finally {
    loading.value = false
  }
}
</script>

<style scoped>
.card {
  border-radius: 15px;
  background-color: #ffffff;
}

h3 {
  color: #0d6efd;
  font-weight: 600;
}

.form-label {
  font-weight: 500;
}

.is-invalid {
  border-color: #dc3545 !important;
}

.form-label{
   position: static;
}
</style>
